import { arktypeResolver } from '@hookform/resolvers/arktype';
import { Box, Collapse, TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import {
  useCreateRecipientMutation,
  useGetCountryStatesQuery,
  useSwiftCodeLookupQuery,
} from '@/api/v1';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { useCustomer } from '@/hooks/useCustomer';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import {
  IBAN_VALIDATION_INVALID_COUNTRY,
  IBAN_VALIDATION_INVALID_IBAN,
  IBAN_VALIDATION_NOT_FOUND,
  IBANSwiftValidationException,
  INVALID_SWIFT_CODE_FOR_IBAN,
  useIBANAndSwiftValidation,
} from '@/hooks/useIBANAndSwiftValidation';
import { PaymentMethod, usePaymentMethodsByCountry } from '@/hooks/usePaymentMethods';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  FiatPaymentForm,
  isFiatPixPaymentForm,
  isFiatSpeiPaymentForm,
  isFiatSwiftPaymentForm,
  paymentMethodFiatSchema,
} from '@/schemas/paymentMethod';
import { FiatAccountDetailFields } from '@/Screens/Dashboard/Contacts/FiatAccountDetailFields';
import { emptyByFiatAccountType } from '@/Screens/Dashboard/Flows/TransferFlow/helpers';
import { Contact, ContactType, SelfContact } from '@/types/contacts';
import { CountryType } from '@/types/country';
import { isCountryType } from '@/types/country.ext';
import { CurrencyCodeType } from '@/types/currency';
import { isCurrencyCodeType } from '@/types/currency.ext';
import { FiatAccountType } from '@/types/fiatAccountType';
import { isFiatAccountType } from '@/types/fiatAccountType.ext';
import { isProbablyValidIBAN } from '@/utility/iban';
import { isFetchError } from '@/utility/reactToolkit';
import { buildCreateRecipientRequest } from '@/utility/recipientBuilder';
import { isUsBic, isValidSwiftCode, normalizeSwiftCode, remapSwiftCode } from '@/utility/swift';

const ACRONYMS: Record<string, string> = {
  iban: 'IBAN',
  cvu: 'CVU',
  clabe: 'CLABE',
  swift: 'SWIFT',
  spei: 'SPEI',
  cpf: 'CPF',
  cnpj: 'CNPJ',
};

const humanizeWord = (word: string): string => ACRONYMS[word.toLowerCase()] ?? word.toLowerCase();

const humanizeFieldName = (field: string): string =>
  field
    .split('.')
    .flatMap((segment: string): string[] => segment.split(/(?<=[a-z])(?=[A-Z])/))
    .map(humanizeWord)
    .join(' ');

const isCamelCaseOrDotted = (word: string): boolean =>
  word.includes('.') || (/[a-z]/.test(word) && /[A-Z]/.test(word));

const isKnownAcronym = (word: string): boolean => word.toLowerCase() in ACRONYMS;

const humanizeValidationMessage = (message: string): string => {
  const humanized = message.replace(/[a-zA-Z]+(?:\.[a-zA-Z0-9]+)*/g, (match: string): string => {
    if (isCamelCaseOrDotted(match)) {
      return humanizeFieldName(match);
    }
    if (isKnownAcronym(match)) {
      return humanizeWord(match);
    }
    return match;
  });
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
};

interface Props {
  readonly redirectPath: string;
  readonly contact: Contact | SelfContact | undefined;

  onCreated(id: string): void;
}

export const PaymentMethodFiat: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onCreated, redirectPath, contact } = props;
  const customer = useCustomer();
  const tr = useTranslator();
  const toast = useToast();
  const { country, accountType, contactId = null } = useParams();
  const [searchParams] = useSearchParams();

  // We guard the values to be able to initialize the form if needed. Don't worry, we validate and redirect
  // if both country or accountType are invalid.
  const guardedCountry = country && isCountryType(country) ? country : CountryType.US;
  const guardedAccountType =
    accountType && isFiatAccountType(accountType) ? accountType : FiatAccountType.ach;

  const guardedPaymentMethod = usePaymentMethodsByCountry(guardedCountry);
  const rawCurrency = searchParams.get('currency');
  const urlCurrency = isCurrencyCodeType(rawCurrency) ? rawCurrency : null;
  const guardedCurrency =
    urlCurrency ??
    guardedPaymentMethod.find(
      (method: PaymentMethod): boolean => method.accountType === guardedAccountType,
    )?.currency ??
    CurrencyCodeType.USD;
  const validateIbanAndSwift = useIBANAndSwiftValidation();

  const [createRecipient, mutation] = useCreateRecipientMutation();
  const [nickname, setNickname] = React.useState<string>('');
  const [swiftLookupMessage, setSwiftLookupMessage] = React.useState<string | null>(null);
  const [isIbanValidating, setIsIbanValidating] = React.useState<boolean>(false);
  // Morph state: customer entered a US-coded BIC on the SWIFT form, so we transitioned
  // them to the WIRE form. Drives the notice banner; null when no morph has occurred.
  // The `routingPrefilled` flag distinguishes "we filled the ABA from our lookup" from
  // "BIC was a US bank we don't recognize — please provide ABA yourself."
  const [morphedFromSwift, setMorphedFromSwift] = React.useState<{
    routingPrefilled: boolean;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: states = [],
    isLoading: statesLoading,
    isError: statesError,
    refetch: refetchStates,
  } = useGetCountryStatesQuery(guardedCountry, { skip: !guardedCountry });
  const hasStates = states.length > 0;

  const NICKNAME_MAX_LENGTH = 40;

  const onNicknameChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    setNickname(event.target.value);
  }, []);

  const form = useFormWithErrors<FiatPaymentForm>({
    defaultValues: emptyByFiatAccountType(
      guardedAccountType,
      guardedCountry,
      contactId,
      guardedCurrency,
    ),
    resolver: arktypeResolver<FiatPaymentForm, unknown, FiatPaymentForm>(paymentMethodFiatSchema),
  });
  const {
    clearErrors,
    formState,
    getFieldState,
    getValues,
    handleSubmit,
    setValue,
    setError,
    watch,
  } = form;

  const onIbanBlur = React.useCallback(async (): Promise<void> => {
    const formData = getValues();
    if (formData.paymentMethod !== FiatAccountType.swift) {
      return;
    }

    const normalizedSwift = normalizeSwiftCode(formData.swiftCode ?? '');
    const swiftForValidation = isValidSwiftCode(normalizedSwift) ? normalizedSwift : '';
    if (!isProbablyValidIBAN(formData.iban) || !formData.country) {
      setSwiftLookupMessage(null);
      return;
    }

    setIsIbanValidating(true);
    try {
      const lookupSwiftCode = await validateIbanAndSwift(
        formData.iban,
        swiftForValidation,
        formData.country,
      );
      if (lookupSwiftCode) {
        setValue('swiftCode', lookupSwiftCode, { shouldTouch: true, shouldValidate: true });
        if (lookupSwiftCode !== normalizedSwift) {
          setSwiftLookupMessage(tr('SWIFT_UPDATED_BADGE'));
        } else {
          setSwiftLookupMessage(null);
        }
      } else {
        setSwiftLookupMessage(null);
      }
      const ibanError = getFieldState('iban').error?.message;
      const swiftError = getFieldState('swiftCode').error?.message;
      if (
        ibanError === IBAN_VALIDATION_INVALID_IBAN ||
        ibanError === IBAN_VALIDATION_NOT_FOUND ||
        ibanError === IBAN_VALIDATION_INVALID_COUNTRY
      ) {
        clearErrors('iban');
      }
      if (swiftError === INVALID_SWIFT_CODE_FOR_IBAN) {
        clearErrors('swiftCode');
      }
    } catch (error: unknown) {
      setSwiftLookupMessage(null);
      if (error instanceof IBANSwiftValidationException) {
        setError(error.field, {
          type: 'manual',
          message: tr(error.message, ...error.parameters),
        });
      }
    } finally {
      setIsIbanValidating(false);
    }
  }, [clearErrors, getFieldState, getValues, setError, setValue, tr, validateIbanAndSwift]);

  const onIbanChange = React.useCallback((): void => {
    setSwiftLookupMessage(null);
  }, []);

  const onSwiftCodeChange = React.useCallback((): void => {
    setSwiftLookupMessage(null);
  }, []);

  const onSubmit = React.useCallback(
    async (formData: FiatPaymentForm): Promise<void> => {
      let submissionData = formData;
      if (!customer.beneficiaryId) {
        console.warn('missing beneficiaryId. Cannot proceed with creating the fiat account.');
        return;
      } else if (!contact) {
        console.warn('missing contact. Cannot proceed with creating the fiat account.');
        return;
      }

      if ('address' in submissionData && hasStates && !submissionData.address?.state) {
        setError('address.state', { type: 'manual', message: tr('VALIDATION_REQUIRED') });
        return;
      }

      const nicknameToSave = nickname.trim() || null;

      // Validate nickname characters
      if (nicknameToSave !== null && !/^[a-zA-Z0-9\s\-_]+$/.test(nicknameToSave)) {
        toast.show(tr('NICKNAME_INVALID_CHARACTERS'), 'error');
        return;
      }

      // Validate PIX document number (CPF: 11 digits, CNPJ: 14 digits)
      if (isFiatPixPaymentForm(submissionData)) {
        const normalized = submissionData.documentNumber.replace(/[.\-/]/g, '');
        if (normalized.length !== 11 && normalized.length !== 14) {
          setError('documentNumber', {
            type: 'manual',
            message: tr('DOCUMENT_NUMBER_INVALID'),
          });
          return;
        }
      }

      // Validate SPEI CLABE (exactly 18 digits)
      if (isFiatSpeiPaymentForm(submissionData)) {
        const normalizedClabe = submissionData.clabe.replace(/[\s-]/g, '');
        if (!/^\d{18}$/.test(normalizedClabe)) {
          setError('clabe', {
            type: 'manual',
            message: tr('CLABE_INVALID'),
          });
          return;
        }
        submissionData = { ...submissionData, clabe: normalizedClabe };
      }

      // Validate SWIFT code format (8 or 11 uppercase alphanumeric characters)
      if (isFiatSwiftPaymentForm(submissionData)) {
        const normalizedSwift = remapSwiftCode(submissionData.swiftCode);
        if (!isValidSwiftCode(normalizedSwift)) {
          setError('swiftCode', {
            type: 'manual',
            message: tr('SWIFT_CODE_INVALID'),
          });
          return;
        }
        submissionData = { ...submissionData, swiftCode: normalizedSwift };
      }

      try {
        if (isFiatSwiftPaymentForm(submissionData)) {
          const lookupSwiftCode = await validateIbanAndSwift(
            submissionData.iban,
            submissionData.swiftCode,
            submissionData.country,
          );
          if (lookupSwiftCode) {
            submissionData = { ...submissionData, swiftCode: lookupSwiftCode };
          }
        }

        const result = await createRecipient(
          buildCreateRecipientRequest(
            submissionData,
            customer.beneficiaryId,
            contact,
            nicknameToSave,
            contact.id ?? undefined,
            customer,
          ),
        ).unwrap();
        onCreated(result.id);
      } catch (error: unknown) {
        if (error instanceof IBANSwiftValidationException) {
          setError(error.field, {
            type: 'manual',
            message: tr(error.message, ...error.parameters),
          });
          return;
        } else if (isFetchError(error) && error.data.code === 'recipient.validation_error') {
          const detail = error.data.message.replace(/^Recipient validation error for\s*/i, '');
          setError('root', { type: 'manual', message: humanizeValidationMessage(detail) });
        } else {
          toast.show(tr('AN_ERROR_OCCURRED'), 'error');
          console.warn(error);
        }
      }
    },
    [
      contact,
      createRecipient,
      customer,
      hasStates,
      nickname,
      onCreated,
      setError,
      toast,
      tr,
      validateIbanAndSwift,
    ],
  );

  React.useEffect((): (() => void) => {
    const subscription = watch((): void => {
      if (formState.errors.root) {
        clearErrors('root');
      }
    });
    return (): void => subscription.unsubscribe();
  }, [watch, clearErrors, formState.errors.root]);

  React.useEffect((): void => {
    // FIXME: do we really need this? Why not just reset the form?
    if (contact?.entity?.type === ContactType.company) {
      setValue('recipientType', 'company');
      setValue('firstName', contact.entity.legalEntityName);
    } else if (contact?.entity?.type === ContactType.individual) {
      setValue('recipientType', 'individual');
      setValue('firstName', contact.entity.firstName);
      setValue('lastName', contact.entity.lastName);
    }
  }, [setValue, contact]);

  // Backend companion: ENG-315. SWIFT recipients pointing at a US bank are gated on the
  // BIC's country segment (positions 5-6). If the customer enters a US-coded BIC on the
  // SWIFT form, we morph the form in place to WIRE — same screen, URL silently updated
  // via navigate(replace), routing number pre-filled from the lookup when available.
  // No "switch to wire?" prompt: the conversion is mandatory.
  const [watchedPaymentMethod, watchedSwiftCode] = watch(['paymentMethod', 'swiftCode']);
  const swiftCodeForMorph = watchedSwiftCode ? normalizeSwiftCode(watchedSwiftCode) : '';
  const shouldRunMorphLookup =
    !morphedFromSwift &&
    watchedPaymentMethod === FiatAccountType.swift &&
    isValidSwiftCode(swiftCodeForMorph) &&
    isUsBic(swiftCodeForMorph);
  const {
    data: morphLookupData,
    isSuccess: morphLookupSuccess,
    isError: morphLookupError,
  } = useSwiftCodeLookupQuery(swiftCodeForMorph, {
    skip: !shouldRunMorphLookup,
  });

  React.useEffect((): void => {
    if (morphedFromSwift) {
      return;
    }
    if (watchedPaymentMethod !== FiatAccountType.swift) {
      return;
    }
    if (!isValidSwiftCode(swiftCodeForMorph) || !isUsBic(swiftCodeForMorph)) {
      return;
    }
    // Wait for the lookup to settle before morphing. Once we morph, the effect early-
    // returns on `morphedFromSwift` and the query is skipped, so a late-arriving routing
    // number wouldn't get pre-filled. Settled = success (data present, routing maybe
    // null) or error (BIC not in lookup table).
    if (!morphLookupSuccess && !morphLookupError) {
      return;
    }
    const swiftLookup = morphLookupData?.data;
    const routingFromLookup = swiftLookup?.routingNumber ?? null;

    setValue('paymentMethod', FiatAccountType.wire);
    setValue('routingNumber', routingFromLookup ?? '');
    setValue('accountNumber', '');
    // Carry the SWIFT response's bank/address fields onto the wire form. Without this
    // the bank-info-by-routing query in USDAccountFields is the only source — and if
    // that response is sparse, the wire address ends up empty. SwiftAccountDetailsFields
    // also populates these on its own, but only if its lookup landed before the morph,
    // which isn't guaranteed (different cache key when the user typed mixed case).
    if (swiftLookup) {
      const bankName = swiftLookup.bank?.name ?? swiftLookup.name;
      if (bankName) {
        setValue('bankName', bankName, { shouldTouch: true });
      }
      if (swiftLookup.address) {
        setValue('address.street', swiftLookup.address.slice(0, 70), { shouldTouch: true });
      }
      if (swiftLookup.cityName) {
        setValue('address.city', swiftLookup.cityName, { shouldTouch: true });
      }
      if (swiftLookup.postcode) {
        setValue('address.zipCode', swiftLookup.postcode, { shouldTouch: true });
      }
    }
    clearErrors(['swiftCode', 'iban', 'isPobo']);
    setSwiftLookupMessage(null);
    setMorphedFromSwift({ routingPrefilled: routingFromLookup !== null });

    const swiftSegment = `/account-type/${FiatAccountType.swift}`;
    const wireSegment = `/account-type/${FiatAccountType.wire}`;
    if (location.pathname.includes(swiftSegment)) {
      navigate(location.pathname.replace(swiftSegment, wireSegment), { replace: true });
    }
  }, [
    watchedPaymentMethod,
    swiftCodeForMorph,
    morphLookupData,
    morphLookupSuccess,
    morphLookupError,
    morphedFromSwift,
    setValue,
    clearErrors,
    location.pathname,
    navigate,
  ]);

  // Invalid country or accountType.
  if (
    !isCountryType(country) ||
    !isFiatAccountType(accountType) ||
    !guardedPaymentMethod.find(
      (method: PaymentMethod): boolean => method.accountType === accountType,
    )
  ) {
    return <Navigate to={redirectPath} replace={true} />;
  }

  return (
    <Box component="form" name="payment-method-fiat" onSubmit={handleSubmit(onSubmit)}>
      <Collapse in={!!morphedFromSwift}>
        <Box mt={2}>
          <Alert severity="info" data-testid="swift-to-wire-morph-notice">
            {morphedFromSwift?.routingPrefilled
              ? tr('SWIFT_TO_WIRE_MORPH_PREFILLED')
              : tr('SWIFT_TO_WIRE_MORPH_NEEDS_ROUTING')}
          </Alert>
        </Box>
      </Collapse>
      <Box mt={2}>
        <FiatAccountDetailFields
          form={form}
          onIbanBlur={onIbanBlur}
          onIbanChange={onIbanChange}
          onSwiftCodeChange={onSwiftCodeChange}
          isIbanValidating={isIbanValidating}
          swiftLookupMessage={swiftLookupMessage}
        />
        <Box mt={4}>
          <TextField
            label={tr('NICKNAME_OPTIONAL')}
            placeholder={tr('NICKNAME_PLACEHOLDER')}
            value={nickname}
            onChange={onNicknameChange}
            inputProps={{ maxLength: NICKNAME_MAX_LENGTH }}
            helperText={`${nickname.length}/${NICKNAME_MAX_LENGTH}`}
            fullWidth
          />
        </Box>
        <Collapse in={!!formState.errors.root?.message}>
          <Box mt={4}>
            <Alert severity="error">{formState.errors.root?.message}</Alert>
          </Box>
        </Collapse>
        {statesError && (
          <Box mt={4}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refetchStates}>
                  {tr('RETRY')}
                </Button>
              }>
              {tr('FAILED_TO_LOAD_STATES')}
            </Alert>
          </Box>
        )}
        <Box mt={6}>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth={true}
            disabled={statesLoading || statesError}
            loading={mutation.status === QueryStatus.pending}>
            {tr('ADD_CONTACT')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
